import { createBrowserRouter, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ApplicationLoadError, LoadingPage } from "@evolys/ui";
import { PermissionRoute } from "@evolys/auth";
import { Providers } from "./providers";
import ErrorPage from "./pages/ErrorPage";
import { isOperationsWorkspace } from "./lib/workspaceMode";

// Lazy load layouts
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const AuthLayout = lazy(() => import("./layouts/AuthLayout"));

// Lazy load pages
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PatientsPage = lazy(() => import("./pages/patients/PatientsPage"));
const PatientCreatePage = lazy(() => import("./pages/patients/PatientCreatePage"));
const PatientDetailPage = lazy(() => import("./pages/patients/PatientDetailPage"));
const PatientEditPage = lazy(() => import("./pages/patients/PatientEditPage"));
const SamplesPage = lazy(() => import("./pages/samples/SamplesPage"));
const SampleDetailPage = lazy(() => import("./pages/samples/SampleDetailPage"));
const OrdersPage = lazy(() => import("./pages/orders/OrdersPage"));
const OrderCreatePage = lazy(() => import("./pages/orders/OrderCreatePage"));
const OrderDetailPage = lazy(() => import("./pages/orders/OrderDetailPage"));
const OrderReceiptPage = lazy(() => import("./pages/orders/OrderReceiptPage"));
const BenchReadPage = lazy(() => import("./pages/bench-read/BenchReadPage"));
const ReferralResultsPage = lazy(
  () => import("./pages/referral-results/ReferralResultsPage"),
);
const ReferralDispatchesPage = lazy(
  () => import("./pages/referral-results/ReferralDispatchesPage"),
);
const SpecimenTransportPage = lazy(
  () => import("./pages/specimen-transport/SpecimenTransportPage"),
);
const InventoryPage = lazy(
  () => import("./pages/inventory/InventoryPage"),
);
const CashierSessionPage = lazy(
  () => import("./pages/CashierSessionPage"),
);
const AppointmentsPage = lazy(
  () => import("./pages/appointments/AppointmentsPage"),
);
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingPage />}>{children}</Suspense>;
}

function AuthorizedRoute({
  permission,
  children,
}: {
  permission: string;
  children: React.ReactNode;
}) {
  return (
    <PermissionRoute permission={permission}>{children}</PermissionRoute>
  );
}

function RootLayout() {
  return (
    <Providers>
      <Outlet />
    </Providers>
  );
}

const dashboardRoute = {
  index: true,
  element: (
    <Lazy>
      <DashboardPage />
    </Lazy>
  ),
};

const specimenRoutes = [
  {
    path: "samples",
    element: (
      <AuthorizedRoute permission="specimens.view">
        <Lazy>
          <SamplesPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "samples/:id",
    element: (
      <AuthorizedRoute permission="specimens.view">
        <Lazy>
          <SampleDetailPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
];

const operationsRoutes = [
  dashboardRoute,
  {
    path: "patients",
    element: (
      <AuthorizedRoute permission="patients.search">
        <Lazy>
          <PatientsPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "patients/create",
    element: (
      <AuthorizedRoute permission="patients.create">
        <Lazy>
          <PatientCreatePage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "patients/:id",
    element: (
      <AuthorizedRoute permission="patients.view">
        <Lazy>
          <PatientDetailPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "patients/:id/edit",
    element: (
      <AuthorizedRoute permission="patients.edit">
        <Lazy>
          <PatientEditPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  ...specimenRoutes,
  {
    path: "orders",
    element: (
      <AuthorizedRoute permission="orders.view">
        <Lazy>
          <OrdersPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "appointments",
    element: (
      <AuthorizedRoute permission="appointments.view">
        <Lazy>
          <AppointmentsPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "orders/new",
    element: (
      <AuthorizedRoute permission="orders.create">
        <Lazy>
          <OrderCreatePage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "orders/:id",
    element: (
      <AuthorizedRoute permission="orders.view">
        <Lazy>
          <OrderDetailPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "orders/:id/receipts/:paymentId",
    element: (
      <AuthorizedRoute permission="receipts.print">
        <Lazy>
          <OrderReceiptPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "specimen-transport",
    element: (
      <AuthorizedRoute permission="specimen_transport.view">
        <Lazy>
          <SpecimenTransportPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "inventory",
    element: (
      <AuthorizedRoute permission="inventory.view">
        <Lazy>
          <InventoryPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "cashier-till",
    element: (
      <AuthorizedRoute permission="finance.view_tills">
        <Lazy>
          <CashierSessionPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "referral-results",
    element: (
      <AuthorizedRoute permission="outsourced_results.view">
        <Lazy>
          <ReferralResultsPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
  {
    path: "referral-dispatches",
    element: (
      <AuthorizedRoute permission="outsourced_results.view">
        <Lazy>
          <ReferralDispatchesPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
];

const bacteriologyRoutes = [
  dashboardRoute,
  ...specimenRoutes,
  {
    path: "bench-read",
    element: (
      <AuthorizedRoute permission="bench_read.access">
        <Lazy>
          <BenchReadPage />
        </Lazy>
      </AuthorizedRoute>
    ),
  },
];

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ApplicationLoadError />,
    children: [
      {
        element: (
          <Lazy>
            <DashboardLayout />
          </Lazy>
        ),
        errorElement: <ErrorPage />,
        children: isOperationsWorkspace
          ? operationsRoutes
          : bacteriologyRoutes,
      },
      {
        element: (
          <Lazy>
            <AuthLayout />
          </Lazy>
        ),
        children: [
          {
            path: "login",
            element: (
              <Lazy>
                <LoginPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: "*",
        element: (
          <Lazy>
            <NotFoundPage />
          </Lazy>
        ),
      },
    ],
  },
], {
  basename:
    import.meta.env.BASE_URL === "/"
      ? undefined
      : import.meta.env.BASE_URL.replace(/\/$/, ""),
});
